/**
 * NotificationBell.jsx — PRODUCTION v2
 * Place at: src/components/NotificationBell.jsx
 *
 * CHANGES vs previous version:
 *
 *   OPT 1 — visibilitychange throttled to 5-minute minimum gap
 *     Old: every single tab return fired fetchNotifications() — no cooldown.
 *     A user bouncing between tabs 10x fired 10 Supabase queries.
 *     New: lastNotifFetchAt ref (in FeedContext) gates the fetch.
 *     If last fetch was < 5 min ago, skip. First mount fetch is always immediate.
 *     UX impact: notifications may be up to 5 min stale after tab return.
 *     Acceptable for a mental health community — notifs are not time-critical.
 *
 *   BUG FIX — formatTimeAgo no longer duplicated
 *     Now imported from CommentSection.jsx which exports it as a shared utility.
 *     (Eliminates the identical function that existed in both files.)
 *
 *   KEPT UNCHANGED:
 *     - Once-per-session fetch guard (notifFetchedRef)
 *     - bulkSetNotifs for accurate unread count
 *     - 2-second delayed mark-as-read
 *     - CSS transition on unread → read clearing
 *     - Outside-click closes dropdown
 *     - All notification types + emoji mapping
 *     - Bell open fallback fetch
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useFeedContext } from "../context/FeedContext";
import { fetchNotifications } from "../lib/feedQueries";
import { formatTimeAgo } from "../lib/utils";

// OPT 1 — minimum gap between visibilitychange-triggered fetches
const NOTIF_FETCH_THROTTLE = 5 * 60 * 1000; // 5 minutes

const NOTIF_EMOJI = {
  upvote:  "👍",
  heart:   "❤️",
  haha:    "😂",
  think:   "🤔",
  wow:     "😮",
  comment: "💬",
  follow:  "👤",
  mention: "📣",
  reply:   "↩️",
  system:  "🔔",
};

export default function NotificationBell({ currentUser }) {
  const {
    notifCache,
    unreadCount,
    bulkSetNotifs,
    markNotifsRead,
    notifFetchedRef,
    lastNotifFetchAt, // OPT 1 — throttle ref from FeedContext
  } = useFeedContext();

  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef      = useRef(null);
  const markReadTimerRef = useRef(null);

  // ── Shared fetch helper ────────────────────────────────────────────────────
  const doFetch = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      setLoading(true);
      const notifs = await fetchNotifications(currentUser.id);
      bulkSetNotifs(notifs); // also sets lastNotifFetchAt in FeedContext
    } catch (err) {
      console.error("fetchNotifications error:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, bulkSetNotifs]);

  // ── 1. Page load fetch (once per session, immediate) ──────────────────────
  useEffect(() => {
    if (!currentUser?.id || notifFetchedRef.current) return;
    notifFetchedRef.current = true;
    doFetch();
  }, [currentUser?.id, notifFetchedRef, doFetch]);

  // ── 2. Tab focus return — visibilitychange (OPT 1: throttled) ────────────
  useEffect(() => {
    if (!currentUser?.id) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      // OPT 1: skip if last fetch was less than 5 minutes ago
      const now     = Date.now();
      const elapsed = now - (lastNotifFetchAt?.current ?? 0);
      if (elapsed < NOTIF_FETCH_THROTTLE) return;
      doFetch();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [currentUser?.id, doFetch, lastNotifFetchAt]);

  // ── 3. Bell open fallback (edge case) ─────────────────────────────────────
  useEffect(() => {
    if (!open || !currentUser?.id || notifFetchedRef.current) return;
    notifFetchedRef.current = true;
    doFetch();
  }, [open, currentUser?.id, notifFetchedRef, doFetch]);

  // ── 2-second delayed mark-as-read ────────────────────────────────────────
  useEffect(() => {
    if (open && unreadCount > 0) {
      markReadTimerRef.current = setTimeout(() => {
        markNotifsRead();
      }, 2000);
    }
    return () => clearTimeout(markReadTimerRef.current);
  }, [open, unreadCount, markNotifsRead]);

  // ── Outside-click closes dropdown ─────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={dropdownRef} style={{ position: "relative", display: "inline-block" }}>

      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position:     "relative",
          background:   "none",
          border:       "none",
          cursor:       "pointer",
          fontSize:     22,
          padding:      "4px 6px",
          borderRadius: 8,
          color:        "var(--text-primary, #111827)",
          transition:   "background 0.15s",
        }}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
      >
        🔔
        {unreadCount > 0 && (
          <span style={{
            position:       "absolute",
            top:            0,
            right:          0,
            background:     "#ef4444",
            color:          "#fff",
            borderRadius:   "50%",
            fontSize:       10,
            fontWeight:     700,
            minWidth:       16,
            height:         16,
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            lineHeight:     1,
            padding:        "0 3px",
          }}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position:     "absolute",
          top:          "calc(100% + 8px)",
          right:        0,
          width:        340,
          maxHeight:    420,
          overflowY:    "auto",
          background:   "var(--bg-primary, #fff)",
          border:       "1px solid var(--border-color, #e5e7eb)",
          borderRadius: 12,
          boxShadow:    "0 8px 32px rgba(0,0,0,0.12)",
          zIndex:       1000,
        }}>

          {/* Header */}
          <div style={{
            padding:        "14px 16px 10px",
            borderBottom:   "1px solid var(--border-color, #e5e7eb)",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
          }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary, #111827)" }}>
              Notifications
            </span>
            <span style={{ fontSize: 12, color: "var(--text-muted, #9ca3af)" }}>
              {loading
                ? "Refreshing…"
                : unreadCount > 0
                  ? "Clearing in a moment…"
                  : "Up to date"}
            </span>
          </div>

          {/* List */}
          {notifCache.length === 0 ? (
            <div style={{
              padding:   "32px 16px",
              textAlign: "center",
              color:     "var(--text-muted, #9ca3af)",
              fontSize:  14,
            }}>
              {loading ? "Loading…" : "No notifications yet"}
            </div>
          ) : (
            notifCache.map(notif => (
              <NotifRow key={notif.id} notif={notif} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Notification row ──────────────────────────────────────────────────────────
function NotifRow({ notif }) {
  const emoji = NOTIF_EMOJI[notif.type] || "🔔";

  return (
    <div style={{
      padding:      "10px 16px",
      display:      "flex",
      gap:          10,
      alignItems:   "flex-start",
      borderBottom: "1px solid var(--border-color, #f3f4f6)",
      background:   notif.is_read
        ? "transparent"
        : "var(--primary-light, rgba(99,102,241,0.06))",
      borderLeft:   notif.is_read
        ? "3px solid transparent"
        : "3px solid var(--primary-color, #6366f1)",
      transition:   "background 0.4s ease, border-color 0.4s ease",
    }}>
      <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{emoji}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--text-primary, #111827)", lineHeight: 1.4 }}>
          {notif.message || buildMessage(notif)}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted, #9ca3af)", marginTop: 3 }}>
          {formatTimeAgo(notif.created_at)}
        </div>
      </div>
    </div>
  );
}

function buildMessage(notif) {
  const actor = notif.actor_username ? `@${notif.actor_username}` : "Someone";
  switch (notif.type) {
    case "upvote":  return `${actor} reacted to your post`;
    case "heart":   return `${actor} loved your post`;
    case "comment": return `${actor} commented on your post`;
    case "follow":  return `${actor} started following you`;
    case "mention": return `${actor} mentioned you`;
    case "reply":   return `${actor} replied to your comment`;
    default:        return `${actor} interacted with your post`;
  }
}